import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Icon } from 'components/ui/Icon'
import { CitationBadge } from 'components/ui/CitationBadge'
import { CitationSource } from 'types/chat-message'

type ToolSourceRowProps = {
  sourceKey: string
  source: CitationSource
}

const ROW_CLASSES = 'tw-flex tw-items-center tw-gap-1.5 tw-text-xs tw-truncate'

const formatValue = (value: unknown): string => {
  if (value == null) return '—'
  if (typeof value === 'object') return JSON.stringify(value)
  return String(value)
}

/**
 * A cited agentic tool call (type: 'tool'). Compact by default; when the call carries
 * arguments (e.g. the Qlik agent's { appId, expression }) the row expands into a small
 * "provenance receipt" so the user can read — and copy — the exact query behind the data.
 * With no params it degrades to the same bare tool-name row as before.
 */
export const ToolSourceRow = ({ sourceKey, source }: ToolSourceRowProps) => {
  const { t } = useTranslation()
  const [copied, setCopied] = useState(false)

  const badge = <CitationBadge label={sourceKey} variant="neutral" size="sm" />
  const gear = (
    <Icon icon={['fas', 'gear']} className="tw-w-3 tw-h-3 tw-flex-shrink-0" style={{ color: 'var(--text-tertiary)' }} />
  )
  const name = <span className="tw-truncate tw-font-mono">{source.name}</span>

  const params = source.params ?? {}
  const entries = Object.entries(params)

  if (entries.length === 0) {
    return (
      <span className={ROW_CLASSES} style={{ color: 'var(--text-muted)' }}>
        {badge}
        {gear}
        {name}
      </span>
    )
  }

  const { expression, ...rest } = params as { expression?: unknown } & Record<string, unknown>
  const restEntries = Object.entries(rest)
  const canCopy = expression != null && typeof navigator !== 'undefined' && !!navigator.clipboard

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(String(expression))
      setCopied(true)
      setTimeout(() => setCopied(false), 1500)
    } catch {
      /* clipboard unavailable — leave the code visible for manual copy */
    }
  }

  return (
    <details className="tw-group">
      <summary
        className={`${ROW_CLASSES} tw-cursor-pointer tw-list-none [&::-webkit-details-marker]:tw-hidden`}
        style={{ color: 'var(--text-muted)' }}
      >
        {badge}
        {gear}
        {name}
        <Icon
          icon={['fas', 'chevron-right']}
          className="tw-w-2.5 tw-h-2.5 tw-flex-shrink-0 tw-transition-transform group-open:tw-rotate-90"
          style={{ color: 'var(--text-tertiary)' }}
        />
      </summary>

      <div className="tw-mt-1 tw-ml-6 tw-rounded tw-border tw-p-2" style={{ borderColor: 'var(--border-default)' }}>
        {restEntries.length > 0 && (
          <dl className="tw-flex tw-flex-col tw-gap-1">
            {restEntries.map(([key, value]) => (
              <div key={key} className="tw-flex tw-gap-2 tw-text-xs tw-leading-relaxed">
                <dt
                  className="tw-font-mono tw-font-semibold tw-flex-shrink-0"
                  style={{ color: 'var(--text-secondary)' }}
                >
                  {key}
                </dt>
                <dd className="tw-min-w-0 tw-break-all" style={{ color: 'var(--text-primary)' }}>
                  {formatValue(value)}
                </dd>
              </div>
            ))}
          </dl>
        )}

        {expression != null && (
          <div className={restEntries.length > 0 ? 'tw-mt-2' : undefined}>
            <div className="tw-flex tw-items-center tw-justify-between tw-mb-1">
              <span className="tw-text-xs tw-font-mono tw-font-semibold" style={{ color: 'var(--text-secondary)' }}>
                expression
              </span>
              {canCopy && (
                <button
                  type="button"
                  onClick={copy}
                  className="tw-inline-flex tw-items-center tw-gap-1 tw-text-xs hover:tw-text-brand tw-transition-colors"
                  style={{ color: 'var(--text-tertiary)' }}
                >
                  <Icon icon={['fas', copied ? 'check' : 'copy']} className="tw-w-3 tw-h-3" />
                  {copied ? t('copied') : t('copy')}
                </button>
              )}
            </div>
            <code className="tw-block tw-bg-neutral-100 tw-rounded tw-p-2 tw-font-mono tw-text-xs tw-overflow-x-auto tw-whitespace-pre-wrap tw-break-words">
              {String(expression)}
            </code>
          </div>
        )}
      </div>
    </details>
  )
}
